// src/components/shopper/OrderComplete.js
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Container, CircularProgress, Typography } from '@mui/material';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

async function ensureOrderSavedAndNotified(piId) {
  try {
    // Get the freshest PI from the server (needs expand: ['payment_method','latest_charge'])
    const r = await fetch(`/stripe/payment-intent/${piId}`);
    if (!r.ok) return;
    const pi = await r.json();

    const md = pi.metadata || {};
    // Items were compacted into metadata as items_json
    let items = [];
    try { items = md.items_json ? JSON.parse(md.items_json) : []; } catch { items = []; }


    // Rebuild shipping address from split metadata fields
    const shippingAddress =
      md.firstName || md.lastName || md.address1 || md.city || md.postalCode || md.country
        ? {
            firstName: md.firstName || '',
            lastName: md.lastName || '',
            address1: md.address1 || '',
            city: md.city || '',
            postalCode: md.postalCode || '',
            country: md.country || '',
          }
        : undefined;

    // Totals
    const total = (pi.amount_received || pi.amount || 0) / 100;
    const shippingCost   = Number(md.shippingCost || 0) || 0;
    const processingFee  = Number(md.processingFee || 0) || 0;
    const weight         = Number(md.weight || 0) || 0;

    // Payment method details (card/paypal/etc.)
    const latestCharge = pi.latest_charge || (pi.charges?.data?.[0] ?? null);
 const pmd = latestCharge?.payment_method_details || {};
 const pmType =
   (Array.isArray(pi.payment_method_types) && pi.payment_method_types[0]) ||
   pmd?.type ||
   undefined;
 const card = pmd?.card; // only present for cards
 const paypalPayerEmail =
   pmd?.paypal?.payer_email ||
   latestCharge?.billing_details?.email ||
   pi.receipt_email ||
   md.email ||
   undefined;
    // Build order payload; compute item subtotals safely
     const cleanedItems = (items || []).map((i) => {
      const name = i.name ?? i.n ?? 'Item';
      const qty  = Number(i.qty ?? i.q ?? 0);
      const raw  = i.unitPrice ?? i.p ?? i.price ?? 0;
      const unitPrice =
        Number.isFinite(Number(raw))
          ? Number(raw)
          : Number(String(raw).replace(/[^\d.]/g, '')) || 0;
      const subtotal = Math.round(qty * unitPrice * 100) / 100;
      const img = i.img ?? i.i ?? i.image ?? i.thumbnail;
      return { name, qty, unitPrice, subtotal, img };
    });


    const payload = {
      id: pi.id,
      email: pi.receipt_email || md.email || undefined,
      phoneNumber: md.phone || undefined,
      items: cleanedItems,
      shippingCost,
      total,
      processingFee,
      weight,
       paymentMethod:
     pmType === 'card'
       ? {
           type: 'card',
           last4: card?.last4,
           brand: card?.brand,
           expMonth: card?.exp_month,
           expYear: card?.exp_year,
         }
       : pmType === 'paypal'       ? {
           type: 'paypal',
           payerEmail: paypalPayerEmail,
         }
       : { type: pmType || 'unknown' },
      shippingAddress,
      createdAt: Date.now(),
    };

    // Upsert order (idempotent for same id)
    await fetch('/orders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    // Redeem voucher (best-effort)
    if (md.voucherCode) {
      try {
        await fetch(`/giftcards/${encodeURIComponent(md.voucherCode)}/redeem`, {
          method: 'PATCH',
        });
      } catch {}
    }

    // Send order email (best-effort)
    if (payload.email) {
      try {
        await fetch('/orders/email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderId: pi.id, to: payload.email }),
        });
      } catch {}
    }
  } catch (e) {
    // Don’t block redirect
    console.warn('ensureOrderSavedAndNotified failed', e);
  }
}

function Inner() {
  const stripe = useStripe();
  const { search } = useLocation();
  const qs = new URLSearchParams(search);
  const clientSecret = qs.get('payment_intent_client_secret');
  const piId = qs.get('payment_intent'); // Stripe usually includes this too

  useEffect(() => {
    let cancelled = false;

    async function decide() {
      if (!stripe) return setTimeout(decide, 80);

      // If we have client secret, check directly via Stripe.js first
      if (clientSecret) {
        const { paymentIntent, error } = await stripe.retrievePaymentIntent(clientSecret);
        if (cancelled) return;

        if (error) {
          window.location.replace('/order-confirmation/failed');
          return;
        }

        const status = paymentIntent?.status;

        if (status === 'succeeded') {
          await ensureOrderSavedAndNotified(paymentIntent.id);
          window.location.replace(`/order-confirmation/success?order_id=${paymentIntent.id}`);
          return;
        }
        if (status === 'requires_payment_method' || status === 'canceled') {
          window.location.replace('/order-confirmation/failed');
          return;
        }

        // e.g. 'processing' or 'requires_capture' — check our server copy (may already be finalized)
        try {
          const r = await fetch(`/stripe/payment-intent/${paymentIntent.id}`);
          if (r.ok) {
            const pi = await r.json();
            if (pi.status === 'succeeded') {
              await ensureOrderSavedAndNotified(pi.id);
              window.location.replace(`/order-confirmation/success?order_id=${pi.id}`);
              return;
            }
            if (pi.status === 'requires_payment_method' || pi.status === 'canceled') {
              window.location.replace('/order-confirmation/failed');
              return;
            }
          }
        } catch {}

        // Final fallback: give the webhook a moment, then assume success and ensure order
        setTimeout(async () => {
          await ensureOrderSavedAndNotified(paymentIntent?.id || piId);
          window.location.replace(
            `/order-confirmation/success?order_id=${paymentIntent?.id || piId}`
          );
        }, 1200);
        return;
      }

      // No client_secret? (Some PSP redirects) – check server using the PI id
      if (piId) {
        try {
          const r = await fetch(`/stripe/payment-intent/${piId}`);
          if (r.ok) {
            const pi = await r.json();
            if (pi.status === 'succeeded') {
              await ensureOrderSavedAndNotified(pi.id);
              window.location.replace(`/order-confirmation/success?order_id=${pi.id}`);
              return;
            }
          }
        } catch {}
      }

      window.location.replace('/order-confirmation/failed');
    }

    decide();
    return () => { cancelled = true; };
  }, [stripe, clientSecret, piId]);

  return (
    <Container sx={{ py: 8, textAlign: 'center' }}>
      <CircularProgress size={60} thickness={4} />
      <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>
        Finalizing your payment…
      </Typography>
    </Container>
  );
}

export default function OrderComplete() {
  return (
    <Elements stripe={stripePromise}>
      <Inner />
    </Elements>
  );
}
